import {ICoreSignalPerson} from 'interfaces/ICoreSignalPerson'
import {iOppAddress, iOppContact} from 'interfaces/IOpportunityFE'
import {DateTime} from 'luxon'
import {emGetInitials} from 'utilities/extensionMethods'
import {FileUpload, IEducation, IExperience, SimpleUserModel} from '../../auth'

export class RequirementsExtensionResponse {
  response_text: string

  response_separator?: string

  constructor(data: any) {
    this.response_text = data.response_text
    this.response_separator = data.response_separator // This is typically two spaces if it's the end of a sentence, or a newline if it's the end of a paragraph.
  }
}

export class ConceptsFromOpenAIResponse {
  discovery_request: DiscoveryRequest

  constructor(data: any) {
    console.log('ConceptsFromOpenAIResponse data:', data)
    this.discovery_request = new DiscoveryRequest(data.discovery_request)
  }
}

export class ConceptsFromBackendResponse {
  constructor(data: any) {
    console.log(data)
  }
}

export class DiscoveryRequestRelatedEntity {
  // This is the base class for both related concepts and similar companies
  unique_id: string
  graph_id: string
  concept_text: string
  prioritization_order: number
  excluded_as_unrelated: boolean
  is_keyword: boolean

  avatar_url?: string
  is_placeholder_item?: boolean
  added_by_user?: SimpleUserModel
  weight?: number

  constructor(data: any) {
    this.unique_id = data.unique_id
    this.graph_id = data.graph_id
    this.concept_text = data.concept_text
    this.prioritization_order = data.prioritization_order
    this.excluded_as_unrelated = !!data.datetime_excluded_as_unrelated
    this.is_keyword = data.is_keyword ?? false
    this.avatar_url = data.avatar_url
    this.is_placeholder_item = data.is_placeholder_item
    this.added_by_user = data.added_by_user ? new SimpleUserModel(data.added_by_user) : undefined
    this.weight = data.weight ?? 1
  }

  getName(): string {
    return this.concept_text
  }
}

export class DiscoveryRequestConcept extends DiscoveryRequestRelatedEntity {}

export class DiscoveryRequestCompany extends DiscoveryRequestRelatedEntity {}

export class DiscoveryRequest {
  created_at: string
  updated_at: string

  unique_id: string
  inputted_text_from_user: string
  latest_inputted_text_from_user: string // Typically inputted_text_from_user, but with revisions may be different
  revision_count: number

  active_concepts: DiscoveryRequestConcept[]

  included_concepts_string?: string
  included_companies_string?: string

  summary_of_inputted_text: string
  likely_company_search: boolean

  constructor(data: any) {
    this.created_at = data.created_at
    this.updated_at = data.updated_at
    this.unique_id = data.unique_id
    this.inputted_text_from_user = data.inputted_text_from_user
    this.latest_inputted_text_from_user = data.latest_inputted_text_from_user
    this.revision_count = data.revision_count || 0
    this.active_concepts = data.active_concepts.map(
      (concept: any) => new DiscoveryRequestConcept(concept)
    )
    this.included_concepts_string = data.included_concepts_string
    this.included_companies_string = data.included_companies_string
    this.summary_of_inputted_text = data.summary_of_inputted_text
    this.likely_company_search = data.likely_company_search
  }

  getSimplifiedInputText(): string {
    if (this.summary_of_inputted_text && this.summary_of_inputted_text.length > 0) {
      return this.summary_of_inputted_text
    }
    // Truncate the input for better display
    const needsEllipsis = this.inputted_text_from_user.length > 100
    const simplifiedInputText = needsEllipsis
      ? this.inputted_text_from_user.substring(0, 100) + '...'
      : this.inputted_text_from_user
    return simplifiedInputText
  }

  getCleanLastUpdated(): string {
    return `${DateTime.fromISO(this.updated_at).toRelative()}`
  }
}

export class DiscoveryRequestRevision {
  unique_id: string
  inputted_text_from_user: string
  revision_count: number
  updated_at: string

  constructor(data: any) {
    this.unique_id = data.unique_id
    this.inputted_text_from_user = data.inputted_text_from_user
    this.revision_count = data.revision_count
    this.updated_at = data.updated_at
  }

  getCleanLastUpdated(): string {
    return `${DateTime.fromISO(this.updated_at).toRelative()}`
  }
}

export class ExpandedCompanyPerson {
  name: string
  role: string
  avatar: string

  constructor(data: any) {
    this.name = data.name
    this.role = data.role
    this.avatar = data.avatar
  }
}

export class ExpandedCompanyHardcodedResult {
  // Temporary class for hard-coded patents/awards/papers/contracts
  unique_id: string
  title: string
  subtitle: string
  description: string

  constructor(data: any) {
    this.unique_id = data.unique_id
    this.title = data.title
    this.subtitle = data.subtitle
    this.description = data.description
  }
}

export class ExpandedCompanyAnalytics {
  total_awarded_amount: number
  total_awards: number
  awards_by_year: Array<{year: number; awards: number; total_awarded_amount: number}>

  constructor(data: any) {
    this.total_awarded_amount = data.total_awarded_amount
    this.total_awards = data.total_awards
    this.awards_by_year = data.awards_by_year ? data.awards_by_year : []
  }
}

export class GraphAward {
  abstract: string
  agency_branch_name: string
  agency_name: string
  agency_tracking_number: string
  amount: number
  business_standard_business_name: string
  contract: string
  contract_end_date: string
  id: string
  name: string
  phase: string
  program: string
  proposal_date: string
  solicitation_number: string
  solicitation_year: number
  timestamp: string
  topic_code: string
  url: string
  year: number

  constructor(data: any) {
    this.abstract = data.abstract
    this.agency_branch_name = data.agency_branch_name
    this.agency_name = data.agency_name
    this.agency_tracking_number = data.agency_tracking_number
    this.amount = data.amount
    this.business_standard_business_name = data.business_standard_business_name
    this.contract = data.contract
    this.contract_end_date = data.contract_end_date
    this.id = data.id
    this.name = data.name
    this.phase = data.phase
    this.program = data.program
    this.proposal_date = data.proposal_date
    this.solicitation_number = data.solicitation_number
    this.solicitation_year = data.solicitation_year
    this.timestamp = data.timestamp
    this.topic_code = data.topic_code
    this.url = data.url
    this.year = data.year
  }
}

export class GraphAppData {
  is_favorited: boolean

  constructor(data: any) {
    this.is_favorited = data.is_favorited
  }
}

class CompanyFederalProfile {
  company_information: {
    name: string
    federal_uei: string
    state_of_incorporation: string
    cage_code: string
    address: string
  }
  important_dates: {
    founded: string
    registration_date: string
    activation_date: string
    expiration_date: string
    last_updated: string
  }
  business_types: string[]
  primary_point_of_contact: string
  alternate_point_of_contact: string
  past_performance_point_of_contact: string
  electronic_point_of_contact: string
  contact_information: string[]
  naics_codes: {code: string; description: string; is_primary: boolean}[]
  product_or_service_codes: string[]

  constructor(data: any) {
    this.company_information = data.company_information
    this.important_dates = data.important_dates
    this.business_types = data.business_types
    this.primary_point_of_contact = data.primary_point_of_contact
    this.alternate_point_of_contact = data.alternate_point_of_contact
    this.past_performance_point_of_contact = data.past_performance_point_of_contact
    this.electronic_point_of_contact = data.electronic_point_of_contact
    this.contact_information = data.contact_information
    this.naics_codes = data.naics_codes
    this.product_or_service_codes = data.product_or_service_codes
  }

  getPrimaryCodeString() {
    const primaryCode = this.naics_codes.find((naicsCode) => naicsCode.is_primary)
    return primaryCode ? `${primaryCode.code} - ${primaryCode.description}` : 'none'
  }
}

export class GraphEntity {
  acquired_by: string
  acquired_by_website: string
  address_city: string
  address_country: string
  address_line_1: string
  address_line_2: string
  address_postal_code: string
  address_state_province: string
  awards_count: number
  cage_code: string
  city: string
  congressional_district: string
  country: string
  crunchbase_active: string
  description: string
  estimated_revenue_category: string
  id: string
  industries: string[]
  mailing_address_city: string
  mailing_address_country: string
  mailing_address_line_1: string
  mailing_address_line_2: string
  mailing_address_postal_code: string
  mailing_address_state_province: string
  naics_codes: string[]
  name: string
  point_of_contact_address_country: string
  point_of_contact_address_line_1: string
  point_of_contact_address_line_2: string
  point_of_contact_address_postal_code: string
  point_of_contact_city: string
  point_of_contact_name_first: string
  point_of_contact_name_last: string
  point_of_contact_name_mi: string
  point_of_contact_state_province: string
  psc_codes: string[]
  sam_identifier: string
  sam_last_updated: string
  sam_registration_date: string
  standard_name: string
  standard_web_domain: string
  state_province: string
  summary: string
  timestamp: string
  total_funding_original_currency: string
  website: string

  appData: GraphAppData | null
  awards: GraphAward[]
  concepts: GraphConceptEntity[]
  related_businesses: GraphCompanyEntity[]
  similar_businesses: {
    business: GraphCompanyEntity
    linking_documents: {
      document: {
        id: string
        name: string
        source_type: string
        source_id: string
        snippet: string
      }
      linking_concepts: GraphConceptEntity[]
    }
  }[]

  // TODO: (in back-end)
  avatar_url: string

  // TODO: Remove

  people: ExpandedCompanyPerson[]
  patents: ExpandedCompanyHardcodedResult[]
  papers: ExpandedCompanyHardcodedResult[]
  contracts: ExpandedCompanyHardcodedResult[]
  links: any[]

  companyFederalProfile: CompanyFederalProfile | null

  business?: any // Occasional business data we include along some awards. Right now used for project awards.

  constructor(
    data: any,
    companyFederalProfile?: any,
    appData?: any,
    awards?: any,
    concepts?: any,
    relatedBusinesses?: any
  ) {
    // super()
    this.acquired_by = data.acquired_by
    this.acquired_by_website = data.acquired_by_website
    this.address_city = data.address_city
    this.address_country = data.address_country
    this.address_line_1 = data.address_line_1
    this.address_line_2 = data.address_line_2
    this.address_postal_code = data.address_postal_code
    this.address_state_province = data.address_state_province
    this.cage_code = data.cage_code
    this.awards_count = data.awards_count
    this.city = data.city
    this.congressional_district = data.congressional_district
    this.country = data.country
    this.crunchbase_active = data.crunchbase_active
    this.description = data.description
    this.estimated_revenue_category = data.estimated_revenue_category
    this.id = data.id
    this.industries = data.industries
    this.mailing_address_city = data.mailing_address_city
    this.mailing_address_country = data.mailing_address_country
    this.mailing_address_line_1 = data.mailing_address_line_1
    this.mailing_address_line_2 = data.mailing_address_line_2
    this.mailing_address_postal_code = data.mailing_address_postal_code
    this.mailing_address_state_province = data.mailing_address_state_province
    this.naics_codes = data.naics_codes
    this.name = data.name
    this.point_of_contact_address_country = data.point_of_contact_address_country
    this.point_of_contact_address_line_1 = data.point_of_contact_address_line_1
    this.point_of_contact_address_line_2 = data.point_of_contact_address_line_2
    this.point_of_contact_address_postal_code = data.point_of_contact_address_postal_code
    this.point_of_contact_city = data.point_of_contact_city
    this.point_of_contact_name_first = data.point_of_contact_name_first
    this.point_of_contact_name_last = data.point_of_contact_name_last
    this.point_of_contact_name_mi = data.point_of_contact_name_mi
    this.point_of_contact_state_province = data.point_of_contact_state_province
    this.psc_codes = data.psc_codes
    this.sam_identifier = data.sam_identifier
    this.sam_last_updated = data.sam_last_updated
    this.sam_registration_date = data.sam_registration_date
    this.similar_businesses = data.similar_businesses || []
    this.standard_name = data.standard_name
    this.standard_web_domain = data.standard
    this.state_province = data.state_province
    this.summary = data.summary
    this.timestamp = data.timestamp
    this.total_funding_original_currency = data.total_funding_original_currency
    this.website = data.website || data.url
    // TODO:
    this.avatar_url = data.avatar_url
    // TODO: Remove
    this.people = []
    this.patents = []
    this.papers = []
    this.contracts = []
    this.links = []
    // The way we handle appData is hacky and temporary. And right now we only keep track of
    // is_favorited. We should probably figure out a better data model for this.
    //
    this.appData = data.appData || appData ? new GraphAppData(data.appData || appData) : null
    this.awards = awards ? awards.map((award: any) => new GraphAward(award)) : []
    this.concepts = concepts ? concepts.map((concept: any) => new GraphConceptEntity(concept)) : []
    this.related_businesses = relatedBusinesses
      ? relatedBusinesses.map((business: any) => new GraphCompanyEntity(business))
      : []
    this.companyFederalProfile = companyFederalProfile
      ? new CompanyFederalProfile(companyFederalProfile)
      : null
    this.business = data.business || undefined
  }

  // create static method called createFromResponse

  static createFromResponse(data: any) {
    return new GraphCompanyEntity(
      data.external_company,
      data.company_federal_profile,
      data.app_data,
      data.awards,
      data.concepts, // score and hits are included but not used
      data.related_companies?.map((x: any) => x.business) // score and hits are included but not used
    )
  }

  getInitials(): string {
    return emGetInitials(this.name || '')
  }

  getCompanyLink(): string {
    return `/businesses/detail?externalId=${this.id}`
  }

  getConceptLink(): string {
    // We are revising how GraphEntity and GraphCompanyEntity/GraphConceptEntity are related,
    // but for now we implement this to make TypeScript happy
    throw new Error('Method not implemented.')
  }
}

// Stubbed out different nodes:

export class GraphCompanyEntity extends GraphEntity {
  get website_with_protocol(): string {
    if (this.website) {
      if (this.website.startsWith('http://') || this.website.startsWith('https://')) {
        return this.website
      }
      return `http://${this.website}`
    }
    return ''
  }
}

export class MPersonDetailGraphEntity {
  id: string
  person_id?: string
  city?: string
  state?: string
  title?: string
  country?: string
  postal_code?: string
  address_line_1?: string
  address_line_2?: string
  domain?: string
  email?: string
  alternate_email?: string
  first_name?: string
  middle_name?: string
  headline?: string
  last_name?: string
  name?: string
  description?: string
  phone_number?: string
  profile_picture_url?: string
  google_scholar_link?: string
  languages?: string[]
  certifications?: {
    id: string
    name: string
    city: string
    state: string
    beginDate: string
    endDate: string
  }[]

  experience?: IExperience[]

  education?: IEducation[]

  skills?: string[]
  patents?: {
    id: string
    title?: string
    url?: string
  }[]

  publications?: {
    id: string
    title?: string
    url?: string
  }[]
  org_social_media?: {
    org_facebook?: string
    org_linkdedin?: string
    org_twitter?: string
    org_website?: string
  }
  organization_name?: string
  social_media: {
    linkedin?: string
    twitter?: string
    facebook?: string
  }

  __owner_id?: string
  __claimable: boolean
  __exists_in_graph: boolean
  __invited_owner_id?: string
  __owner_data?: MPersonDetailGraphEntity
  additionalInfo?: {
    source: string
    createdDtTm: string
    data: any
  }[]
  rchilli_parsed_resume?: any
  brightdata_profile_parsed?: any

  constructor(response: any) {
    this.id = response.id
    this.__invited_owner_id = response.__invited_owner_id ?? undefined
    this.__owner_data = response.__owner_data ?? {}
    this.person_id = response.person_id ?? undefined
    this.__owner_id = response.__owner_id ?? undefined
    this.__claimable = response.__claimable ?? false
    this.__exists_in_graph = response.__claimable ?? false
    this.city = response.__owner_data?.city ?? response.city ?? undefined
    this.postal_code = response.__owner_data?.postal_code ?? response.postal_code ?? undefined
    this.country = response.__owner_data?.country ?? response.country ?? undefined
    this.address_line_1 =
      response.__owner_data?.address_line_1 ?? response.address_line_1 ?? undefined
    this.address_line_2 =
      response.__owner_data?.address_line_2 ?? response.address_line_2 ?? undefined
    this.domain = response.__owner_data?.domain ?? response.domain ?? undefined
    this.email = response.__owner_data?.email ?? response.email ?? undefined
    this.first_name = response.__owner_data?.first_name ?? response.first_name ?? undefined
    this.headline = response.__owner_data?.headline ?? response.headline ?? undefined
    this.last_name = response.__owner_data?.last_name ?? response.last_name ?? undefined
    this.name = response.__owner_data?.name ?? response.name ?? undefined
    this.description = response.__owner_data?.description ?? response.description ?? undefined
    this.phone_number = response.__owner_data?.phone_number ?? response.phone_number ?? undefined
    this.org_social_media =
      response.__owner_data?.org_social_media ?? response.org_social_media ?? undefined
    this.organization_name =
      response.__owner_data?.organization_name ?? response.organization_name ?? undefined
    this.social_media = response.__owner_data?.social_media ?? response.social_media ?? undefined
    this.state = response.__owner_data?.state ?? response.state ?? undefined
    this.title = response.__owner_data?.title ?? response.title ?? undefined
    this.profile_picture_url =
      response.__owner_data?.profile_picture_url?.replace('aws-local', 'localhost') ??
      response.profile_picture_url?.replace('aws-local', 'localhost') ??
      undefined
    this.google_scholar_link =
      response.__owner_data?.google_scholar_link ?? response.google_scholar_link ?? undefined
    this.skills = response.__owner_data?.skills ?? response.skills ?? []
    this.certifications = response.__owner_data?.certifications ?? response.certifications ?? []
    this.experience = response.__owner_data?.experience ?? response.experience ?? []
    this.education = response.__owner_data?.education ?? response.education ?? []
    this.patents = response.__owner_data?.patents ?? response.patents ?? []
    this.publications = response.__owner_data?.publications ?? response.publications ?? []
    this.additionalInfo = response.__owner_data?.additionalInfo ?? response.additionalInfo ?? []
    this.alternate_email = response.__owner_data?.alternate_email ?? response.alternate_email ?? []
    this.rchilli_parsed_resume =
      response.__owner_data?.rchilli_parsed_resume ?? response.rchilli_parsed_resume ?? undefined
    this.brightdata_profile_parsed =
      response.__owner_data?.brightdata_profile_parsed ??
      response.brightdata_profile_parsed ??
      undefined
  }
}

export class MSamEntity {
  id: string
  sam_identifier: string
  name?: string
  standard_business_name?: string
  standard_web_domain?: string
  website?: string
  business_id?: string
  business_node_id?: string
  business_structure?: string
  business_type_codes?: string[]
  business_type_descriptions?: string[]
  cage_codes?: string[]
  congressional_district?: string
  gov_business_contact_address_city?: string
  gov_business_contact_address_country?: string
  gov_business_contact_address_line_1?: string
  gov_business_contact_address_line_2?: string
  gov_business_contact_address_postal_code?: string
  gov_business_contact_address_state_province?: string
  gov_business_contact_name_first?: string
  gov_business_contact_name_last?: string
  gov_business_contact_name_mi?: string
  highest_owner_cage_code?: string
  highest_owner_name?: string
  highest_owner_uei?: string
  immediate_owner_cage_code?: string
  immediate_owner_name?: string
  immediate_owner_uei?: string
  mailing_address_city?: string
  mailing_address_line_1?: string
  mailing_address_line_2?: string
  mailing_address_state_province?: string
  naics_codes?: string[]
  physical_address_city?: string
  physical_address_country?: string
  physical_address_line_1?: string
  physical_address_line_2?: string
  physical_address_postal_code?: string
  physical_address_state_province?: string
  predecessor_cage_code?: string
  predecessor_name?: string
  psc_codes?: string[]
  latitude?: number
  longitude?: number

  constructor(response: any) {
    this.id = response.id ?? ''
    this.sam_identifier = response.sam_identifier ?? ''
    this.name = response.name ?? undefined
    this.standard_business_name = response.standard_business_name ?? undefined
    this.standard_web_domain = response.standard_web_domain ?? undefined
    this.website = response.website ?? undefined
    this.business_id = response.business_id ?? undefined
    this.business_node_id = response.business_node_id ?? undefined
    this.business_structure = response.business_structure ?? undefined
    this.business_type_codes = response.business_type_codes ?? undefined
    this.business_type_descriptions = response.business_type_descriptions ?? undefined
    this.cage_codes = response.cage_codes ?? undefined
    this.congressional_district = response.congressional_district ?? undefined
    this.gov_business_contact_address_city = response.gov_business_contact_address_city ?? undefined
    this.gov_business_contact_address_country =
      response.gov_business_contact_address_country ?? undefined
    this.gov_business_contact_address_line_1 =
      response.gov_business_contact_address_line_1 ?? undefined
    this.gov_business_contact_address_line_2 =
      response.gov_business_contact_address_line_2 ?? undefined
    this.gov_business_contact_address_postal_code =
      response.gov_business_contact_address_postal_code ?? undefined
    this.gov_business_contact_address_state_province =
      response.gov_business_contact_address_state_province ?? undefined
    this.gov_business_contact_name_first = response.gov_business_contact_name_first ?? undefined
    this.gov_business_contact_name_last = response.gov_business_contact_name_last ?? undefined
    this.gov_business_contact_name_mi = response.gov_business_contact_name_mi ?? undefined
    this.highest_owner_cage_code = response.highest_owner_cage_code ?? undefined
    this.highest_owner_name = response.highest_owner_name ?? undefined
    this.highest_owner_uei = response.highest_owner_uei ?? undefined
    this.immediate_owner_cage_code = response.immediate_owner_cage_code ?? undefined
    this.immediate_owner_name = response.immediate_owner_name ?? undefined
    this.immediate_owner_uei = response.immediate_owner_uei ?? undefined
    this.mailing_address_city = response.mailing_address_city ?? undefined
    this.mailing_address_line_1 = response.mailing_address_line_1 ?? undefined
    this.mailing_address_line_2 = response.mailing_address_line_2 ?? undefined
    this.mailing_address_state_province = response.mailing_address_state_province ?? undefined
    this.naics_codes = response.naics_codes ?? undefined
    this.physical_address_city = response.physical_address_city ?? undefined
    this.physical_address_country = response.physical_address_country ?? undefined
    this.physical_address_line_1 = response.physical_address_line_1 ?? undefined
    this.physical_address_line_2 = response.physical_address_line_2 ?? undefined
    this.physical_address_postal_code = response.physical_address_postal_code ?? undefined
    this.physical_address_state_province = response.physical_address_state_province ?? undefined
    this.predecessor_cage_code = response.predecessor_cage_code ?? undefined
    this.predecessor_name = response.predecessor_name ?? undefined
    this.psc_codes = response.psc_codes ?? undefined
  }
}

export class MBusinessDetailGraphEntity {
  __claimable?: boolean
  __exists_in_graph?: boolean
  __invited_owner_id?: string
  __owner_data?: MBusinessDetailGraphEntity
  __owner_id?: string
  address_city?: string
  address_country?: string
  address_line_1?: string
  address_line_2?: string
  address_postal_code?: string
  address_state_province?: string
  agency_breakdown?: any
  annual_revenues?: any
  awards_amount: number
  awards_by_year?: any
  awards_count: number
  business_structure?: string
  business_types?: string[]
  cage_codes?: string[]
  city: string
  congressional_district?: string
  country: string
  crunchbase_active: boolean
  crunchbase_permalink?: string
  description?: string
  email_addresses?: string
  facebook_page?: string
  id?: string
  industries?: string[]
  linkedin_page?: string
  logo: string
  mailing_address_city?: string
  mailing_address_country?: string
  mailing_address_line_1?: string
  mailing_address_postal_code?: string
  mailing_address_state_province?: string
  naics_breakdown?: any
  naics_codes?: string[]
  name: string
  point_of_contact_address_country?: string
  point_of_contact_address_line_1?: string
  point_of_contact_address_line_2?: string
  point_of_contact_address_postal_code?: string
  point_of_contact_city?: string
  point_of_contact_state_province?: string
  point_of_contact_name_first?: string
  point_of_contact_name_last?: string
  point_of_contact_name_mi?: string
  sam_identifier?: string
  sam_last_updated?: string
  sam_registration_date?: string
  standard_name: string
  standard_web_domain: string
  state_province: string
  summary?: string
  tmp_cb_id?: number
  tmp_sam_id?: number
  tmp_sbir_id?: number
  total_funding_original?: number
  total_funding_original_currency?: string
  total_funding_usd?: number
  total_revenue?: number
  website: string
  psc_codes?: string[]
  sam_ids?: string[]
  uei_cage_codes?: string[]
  webnyze_business_id?: string
  dsbs_address_line_1?: string
  dsbs_address_line_2?: string
  dsbs_capabilities_narrative?: string
  dsbs_city?: string
  dsbs_county_code?: number
  dsbs_keywords?: string
  dsbs_metro_stat_area?: number
  dsbs_phone_number?: string
  dsbs_postal_code?: string
  dsbs_state_province?: string
  dsbs_year_established?: number
  linkedin_key_data?: {
    employee_urls?: string[]
    employee_profiles?: ICoreSignalPerson[]
    investor_list?: {
      cb_url?: string
      name?: string
    }[]
    funding_rounds?: {
      cb_url?: string
      round_date?: string
      round_investor_count?: number
      round_money_raised?: number
      round_type?: string
      total_rounds_count?: number
    }[]

    specialties?: string[]
    industry?: string
    size?: string
    type?: string
    founded?: number
    employees_count?: number
  }
  additionalInfo?: {
    source: string
    createdDtTm: string
    data: any
  }[]
  tree?: any

  constructor(response: any) {
    this.__claimable = response.__claimable ?? false
    this.__exists_in_graph = response.__exists_in_graph ?? undefined
    this.__invited_owner_id = response.__invited_owner_id ?? undefined
    this.__owner_data = response.__owner_data ?? {}
    this.__owner_id = response.__owner_id ?? undefined
    this.address_city = response.__owner_data?.address_city ?? response.address_city ?? undefined
    this.address_country =
      response.__owner_data?.address_country ?? response.address_country ?? undefined
    this.address_line_1 =
      response.__owner_data?.address_line_1 ?? response.address_line_1 ?? undefined
    this.address_line_2 =
      response.__owner_data?.address_line_2 ?? response.address_line_2 ?? undefined
    this.address_postal_code =
      response.__owner_data?.address_postal_code ?? response.address_postal_code ?? undefined
    this.address_state_province =
      response.__owner_data?.address_state_province ?? response.address_state_province ?? undefined
    this.agency_breakdown = response.agency_breakdown ?? undefined
    this.annual_revenues = response.annual_revenues ?? undefined
    this.awards_amount = response.awards_amount ?? 0
    this.awards_by_year = response.awards_by_year ?? undefined
    this.awards_count = response.awards_count ?? 0
    this.business_structure = response.business_structure ?? undefined
    this.business_types = response.business_types ?? undefined
    this.cage_codes = response.cage_codes ?? undefined
    this.city = response.__owner_data?.city ?? response.city ?? ''
    this.congressional_district = response.congressional_district ?? undefined
    this.country = response.__owner_data?.city ?? response.country ?? ''
    this.crunchbase_active = response.crunchbase_active ?? false
    this.crunchbase_permalink = response.crunchbase_permalink ?? ''
    this.description = response.__owner_data?.description ?? response.description ?? ''
    this.email_addresses = response.__owner_data?.email_addresses ?? response.email_addresses ?? ''
    this.facebook_page = response.__owner_data?.facebook_page ?? response.facebook_page ?? ''
    this.id = response.id ?? ''
    this.industries = response.industries ?? []
    this.linkedin_page = response.__owner_data?.linkedin_page ?? response.linkedin_page ?? ''
    this.logo =
      response.__owner_data?.logo?.replace('aws-local', 'localhost') ??
      response.logo?.replace('aws-local', 'localhost') ??
      ''
    this.mailing_address_city =
      response.__owner_data?.mailing_address_city ?? response.mailing_address_city ?? undefined
    this.mailing_address_country =
      response.__owner_data?.mailing_address_country ??
      response.mailing_address_country ??
      undefined
    this.mailing_address_line_1 =
      response.__owner_data?.mailing_address_line_1 ?? response.mailing_address_line_1 ?? undefined
    this.mailing_address_postal_code =
      response.__owner_data?.mailing_address_postal_code ??
      response.mailing_address_postal_code ??
      undefined
    this.mailing_address_state_province =
      response.__owner_data?.mailing_address_state_province ??
      response.mailing_address_state_province ??
      undefined
    this.naics_breakdown = response.naics_breakdown ?? []
    this.naics_codes = response.naics_codes ?? undefined
    this.name = response.__owner_data?.name ?? response.name ?? ''
    this.point_of_contact_address_country =
      response.__owner_data?.point_of_contact_address_country ??
      response.point_of_contact_address_country ??
      undefined
    this.point_of_contact_address_line_1 =
      response.__owner_data?.point_of_contact_address_line_1 ??
      response.point_of_contact_address_line_1 ??
      undefined
    this.point_of_contact_address_line_2 =
      response.__owner_data?.point_of_contact_address_line_2 ??
      response.point_of_contact_address_line_2 ??
      undefined
    this.point_of_contact_address_postal_code =
      response.__owner_data?.point_of_contact_address_postal_code ??
      response.point_of_contact_address_postal_code ??
      undefined
    this.point_of_contact_city =
      response.__owner_data?.point_of_contact_city ?? response.point_of_contact_city ?? undefined
    this.point_of_contact_state_province =
      response.__owner_data?.point_of_contact_state_province ??
      response.point_of_contact_state_province ??
      undefined
    this.point_of_contact_name_first =
      response.__owner_data?.point_of_contact_name_first ??
      response.point_of_contact_name_first ??
      undefined
    this.point_of_contact_name_last =
      response.__owner_data?.point_of_contact_name_last ??
      response.point_of_contact_name_last ??
      undefined
    this.point_of_contact_name_mi =
      response.__owner_data?.point_of_contact_name_mi ??
      response.point_of_contact_name_mi ??
      undefined
    this.sam_identifier = response.sam_identifier ?? undefined
    this.sam_last_updated = response.sam_last_updated ?? undefined
    this.sam_registration_date = response.sam_registration_date ?? undefined
    this.standard_name =
      response.__owner_data?.point_of_contact_name_mi ?? response.standard_name ?? ''
    this.standard_web_domain =
      response.__owner_data?.standard_web_domain ?? response.standard_web_domain ?? ''
    this.state_province = response.__owner_data?.state_province ?? response.state_province ?? ''
    this.summary = response.__owner_data?.summary ?? response.summary ?? ''
    this.tmp_cb_id = response.tmp_cb_id ?? undefined
    this.tmp_sam_id = response.tmp_sam_id ?? undefined
    this.tmp_sbir_id = response.tmp_sbir_id ?? undefined
    this.total_funding_original = response.total_funding_original ?? undefined
    this.total_funding_original_currency = response.total_funding_original_currency ?? ''
    this.total_funding_usd =
      response.__owner_data?.total_funding_usd ?? response.total_funding_usd ?? undefined
    this.total_revenue = response.__owner_data?.total_revenue ?? response.total_revenue ?? undefined
    this.website = response.__owner_data?.website ?? response.website ?? ''
    this.psc_codes = response.psc_codes ?? []
    this.sam_ids = response.sam_ids ?? []
    this.uei_cage_codes = response.uei_cage_codes ?? []
    this.webnyze_business_id = response.webnyze_business_id ?? ''
    this.dsbs_address_line_1 = response.dsbs_address_line_1 ?? undefined
    this.dsbs_address_line_2 = response.dsbs_address_line_2 ?? undefined
    this.dsbs_capabilities_narrative = response.dsbs_capabilities_narrative ?? undefined
    this.dsbs_city = response.dsbs_city ?? undefined
    this.dsbs_county_code = response.dsbs_county_code ?? undefined
    this.dsbs_keywords = response.dsbs_keywords ?? undefined
    this.dsbs_metro_stat_area = response.dsbs_metro_stat_area ?? undefined
    this.dsbs_phone_number = response.dsbs_phone_number ?? undefined
    this.dsbs_postal_code = response.dsbs_postal_code ?? undefined
    this.dsbs_state_province = response.dsbs_state_province ?? undefined
    this.dsbs_year_established = response.dsbs_year_established ?? undefined
    this.linkedin_key_data =
      response.__owner_data?.linkedin_key_data ?? response.linkedin_key_data ?? undefined
    this.additionalInfo = response.__owner_data?.additionalInfo ?? response.additionalInfo ?? []
    this.tree = response.tree ?? undefined
  }
}

export class GraphConceptEntity {
  aliases: string[]
  id: string
  last_modified: string
  name: string
  qid: string
  timestamp: string

  related_concepts: GraphConceptEntity[]
  related_businesses: GraphCompanyEntity[]

  appData: GraphAppData | null
  weight?: number

  constructor(data: any, appData?: any, related_concepts?: any, related_companies?: any) {
    this.aliases = data.aliases
    this.id = data.id
    this.last_modified = data.last_modified
    this.name = data.name
    this.qid = data.qid
    this.timestamp = data.timestamp
    this.related_concepts = related_concepts
      ? related_concepts.map((concept: any) => new GraphConceptEntity(concept))
      : []
    this.related_businesses = related_companies
      ? related_companies.map((company: any) => new GraphCompanyEntity(company))
      : []
    this.appData = appData || data.appData ? new GraphAppData(appData || data.appData) : null
    this.weight = data.weight ?? 1
  }

  static createFromResponse(data: any): GraphConceptEntity {
    return new GraphConceptEntity(
      data.external_concept,
      data.app_data,
      data.related_concepts.map((x: any) => x.related_concept), // score and hits are included but not used
      data.related_companies.map((x: any) => x.business) // score and hits are included but not used
    )
  }

  getInitials(): string {
    return emGetInitials(this.name || '')
  }

  getConceptLink(): string {
    return `/concepts/detail?externalId=${this.id}`
  }
}

export class DirectGraphQueryEntityType {
  value: string
  display_name: string

  constructor(data: any) {
    this.value = data.value
    this.display_name = data.display_name
  }
}

export class DirectGraphQueryEntity {
  unique_id: string
  type: DirectGraphQueryEntityType

  title: string
  subtitle: string
  description: string

  constructor(data: any) {
    this.unique_id = data.unique_id
    this.type = new DirectGraphQueryEntityType(data.type)
    this.title = data.title
    this.subtitle = data.subtitle
    this.description = data.description
  }

  static from_response_result(result: any) {
    // Graph entities are represented in a variety of formats so we check the type
    if (result.type.value === 'Business') {
      return new DirectGraphBusinessEntity(result)
    }
    if (result.type.value === 'award') {
      return new DirectGraphAwardEntity(result)
    }
    console.log('UNKNOWN ENTITY TYPE: ', result.type.value)
    console.log(JSON.stringify(result))
    throw new Error('Unknown entity type')
  }

  static from_item_without_type(item: any) {
    // Need to check ID to determine type
    if (item.id.startsWith('BUS::')) {
      return new DirectGraphBusinessEntity({
        ...item,
        type: {
          value: 'business',
          display_name: 'Business',
        },
      })
    }
    if (item.id.startsWith('AWD::')) {
      return new DirectGraphAwardEntity({
        ...item,
        type: {
          value: 'award',
          display_name: 'Award',
        },
      })
    }
    console.log('UNKNOWN ENTITY TYPE: ', item.unique_id)
    console.log(JSON.stringify(item))
    throw new Error('Unknown entity type')
  }

  static from_business_without_type(item: any) {
    return new DirectGraphBusinessEntity({
      ...item,
      type: {
        value: 'business',
        display_name: 'Business',
      },
    })
  }
}

export class EntityDocumentWebpage {
  is_home_page: boolean
  extracted_at: string
  domain: string
  name: string
  base_url: string
  id: string
  business_id: string
  content: string
  url: string

  constructor(data: any) {
    this.is_home_page = data.is_home_page
    this.extracted_at = data.extracted_at
    this.domain = data.domain
    this.name = data.name
    this.base_url = data.base_url
    this.id = data.id
    this.business_id = data.business_id
    this.content = data.content
    this.url = data.url
  }
}

export class EntityDocument {
  name: string
  source_type: string
  id: string
  text: string
  source_id: string
  webpage?: EntityDocumentWebpage
  award?: GraphAward

  constructor(data: any) {
    this.name = data.name
    this.source_type = data.source_type
    this.id = data.id
    this.text = data.text
    this.source_id = data.source_id
    this.webpage = data.webpage ? new EntityDocumentWebpage(data.webpage) : undefined
    this.award = data.award ? new GraphAward(data.award) : undefined
  }
}

export class MBusinessEntityDetail {
  estimated_revenue_category: string
  congressional_district: string
  country: string
  crunchbase_active: boolean
  naics_codes: string[]
  city: string
  tmp_sam_id: number
  point_of_contact_state_province: string
  description: string
  tmp_cb_id: number
  mailing_address_postal_code: string
  address_state_province: string
  mailing_address_state_province: string
  tmp_sbir_id: number
  standard_web_domain: string
  total_funding_original_currency: string
  mailing_address_country: string
  point_of_contact_address_country: string
  sam_identifier: string
  address_line_1: string
  similar_company_count: number
  sam_last_updated: string
  id: string
  point_of_contact_name_first: string
  point_of_contact_address_postal_code: string
  summary: string
  total_funding_usd: number
  website: string
  total_funding_original: number
  point_of_contact_city: string
  address_postal_code: string
  mailing_address_line_1: string
  address_country: string
  state_province: string
  sam_registration_date: string
  mailing_address_city: string
  point_of_contact_name_last: string
  address_city: string
  crunchbase_permalink: string
  industries: string[]
  name: string
  point_of_contact_address_line_2: string
  standard_name: string
  point_of_contact_address_line_1: string
  cage_code: string

  constructor(data: any) {
    this.estimated_revenue_category = data.estimated_revenue_category ?? ''
    this.congressional_district = data.congressional_district ?? ''
    this.country = data.country ?? ''
    this.crunchbase_active = data.crunchbase_active ?? false
    this.naics_codes = data.naics_codes ?? []
    this.city = data.city ?? ''
    this.tmp_sam_id = data.tmp_sam_id ?? 0
    this.point_of_contact_state_province = data.point_of_contact_state_province ?? ''
    this.description = data.description ?? ''
    this.tmp_cb_id = data.tmp_cb_id ?? 0
    this.mailing_address_postal_code = data.mailing_address_postal_code ?? ''
    this.address_state_province = data.address_state_province ?? ''
    this.mailing_address_state_province = data.mailing_address_state_province ?? ''
    this.tmp_sbir_id = data.tmp_sbir_id ?? 0
    this.standard_web_domain = data.standard_web_domain ?? ''
    this.total_funding_original_currency = data.total_funding_original_currency ?? ''
    this.mailing_address_country = data.mailing_address_country ?? ''
    this.point_of_contact_address_country = data.point_of_contact_address_country ?? ''
    this.sam_identifier = data.sam_identifier ?? ''
    this.address_line_1 = data.address_line_1 ?? ''
    this.similar_company_count = data.similar_company_count ?? 0
    this.sam_last_updated = data.sam_last_updated ?? ''
    this.id = data.id ?? ''
    this.point_of_contact_name_first = data.point_of_contact_name_first ?? ''
    this.point_of_contact_address_postal_code = data.point_of_contact_address_postal_code ?? ''
    this.summary = data.summary ?? ''
    this.total_funding_usd = data.total_funding_usd ?? 0
    this.website = data.website ?? ''
    this.total_funding_original = data.total_funding_original ?? 0
    this.point_of_contact_city = data.point_of_contact_city ?? ''
    this.address_postal_code = data.address_postal_code ?? ''
    this.mailing_address_line_1 = data.mailing_address_line_1 ?? ''
    this.address_country = data.address_country ?? ''
    this.state_province = data.state_province ?? ''
    this.sam_registration_date = data.sam_registration_date ?? ''
    this.mailing_address_city = data.mailing_address_city ?? ''
    this.point_of_contact_name_last = data.point_of_contact_name_last ?? ''
    this.address_city = data.address_city ?? ''
    this.crunchbase_permalink = data.crunchbase_permalink ?? ''
    this.industries = data.industries ?? []
    this.name = data.name ?? ''
    this.point_of_contact_address_line_2 = data.point_of_contact_address_line_2 ?? ''
    this.standard_name = data.standard_name ?? ''
    this.point_of_contact_address_line_1 = data.point_of_contact_address_line_1 ?? ''
    this.cage_code = data.cage_code ?? ''
  }

  get website_with_protocol(): string {
    if (this.website) {
      if (this.website.startsWith('http://') || this.website.startsWith('https://')) {
        return this.website
      }
      return `http://${this.website}`
    }
    return ''
  }
}

export class MOpportunityEntityDetail {
  id: string
  name: string
  description: string
  amount: number
  response_deadline_utc: string // "2025-02-07 21:00:00",
  response_deadline_offset: string // "-06:00",
  dept_path: string[]
  notice_id: string
  solicitation_number: string
  url: string
  office_address: iOppAddress
  office_address_id: string
  point_of_contact: iOppContact[]
  timestamp: string //"2025-05-13 02:00:58.753687+00",

  constructor(data: any) {
    this.id = data.id ? data.id : data.__owner_data?.id ? data.__owner_data?.id : ''
    this.name = data.name ? data.name : data.__owner_data?.name ? data.__owner_data?.name : ''
    this.description = data.description
      ? data.description
      : data.__owner_data?.description
      ? data.__owner_data?.description
      : ''
    this.amount = data.amount
      ? data.amount
      : data.__owner_data?.amount
      ? data.__owner_data?.amount
      : 0
    this.notice_id = data.notice_id
      ? data.notice_id
      : data.__owner_data?.notice_id
      ? data.__owner_data?.notice_id
      : ''
    this.office_address = data.office_address
      ? data.office_address
      : data.__owner_data?.office_address
      ? data.__owner_data?.office_address
      : undefined
    this.office_address_id = data.office_address_id
      ? data.office_address_id
      : data.__owner_data?.office_address_id
      ? data.__owner_data?.office_address_id
      : ''
    this.point_of_contact = data.point_of_contact
      ? data.point_of_contact
      : data.__owner_data?.point_of_contact
      ? data.__owner_data?.point_of_contact
      : []
    this.response_deadline_utc = data.response_deadline_utc
      ? data.response_deadline_utc
      : data.__owner_data?.response_deadline_utc
      ? data.__owner_data?.response_deadline_utc
      : ''
    this.response_deadline_offset = data.response_deadline_offset
      ? data.response_deadline_offset
      : data.__owner_data?.response_deadline_offset
      ? data.__owner_data?.response_deadline_offset
      : ''
    this.dept_path = data.dept_path
      ? data.dept_path
      : data.__owner_data?.dept_path
      ? data.__owner_data?.dept_path
      : []
    this.url = data.url ? data.url : data.__owner_data?.url ? data.__owner_data?.url : ''
    this.solicitation_number = data.solicitation_number
      ? data.solicitation_number
      : data.__owner_data?.solicitation_number
      ? data.__owner_data?.solicitation_number
      : ''
    this.timestamp = data.timestamp
      ? data.timestamp
      : data.__owner_data?.timestamp
      ? data.__owner_data?.timestamp
      : ''
  }
}

export class MAwardEntityDetail {
  abstract: string
  agency_branch_name: string
  agency_name: string
  agency_tracking_number: string
  amount: number
  business_standard_business_name: string
  contract: string
  id: string
  business_id: string
  name: string
  phase: string
  program: string
  sbir_id: number
  solicitation_number: string
  topic_code: string
  url: string
  year: number

  constructor(data: any) {
    this.abstract = data.abstract ?? ''
    this.agency_branch_name = data.agency_branch_name ?? ''
    this.agency_name = data.agency_name ?? ''
    this.agency_tracking_number = data.agency_tracking_number ?? ''
    this.amount = data.amount ?? []
    this.business_standard_business_name = data.business_standard_business_name ?? ''
    this.contract = data.contract ?? 0
    this.id = data.id ?? ''
    this.business_id = data.business_id ?? ''
    this.name = data.name ?? ''
    this.phase = data.phase ?? ''
    this.program = data.program ?? ''
    this.sbir_id = data.sbir_id ?? 0
    this.solicitation_number = data.solicitation_number ?? ''
    this.topic_code = data.topic_code ?? ''
    this.url = data.url ?? ''
    this.year = data.year ?? 0
  }

  // localizedAmount(): string {
  //   try {
  //     // try to take $224481.0 into $224,481
  //     const amountWithoutDollarSign = this.amount.replace('$', '')
  //     // parse float
  //     const amountAsFloat = parseFloat(amountWithoutDollarSign)
  //     // format as currency
  //     const amountAsCurrency = amountAsFloat.toLocaleString('en-US', {
  //       style: 'currency',
  //       currency: 'USD',
  //     })
  //     return amountAsCurrency
  //   } catch (e) {
  //     return this.subtitle
  //   }
  // }
}

export class MInnovationEntityDetail {
  id: string
  name: string
  title_link: string
  abstract: string
  background: string
  overview: string
  tech_description: string
  advantages: string
  applications: string
  publications: string
  ip_status: string
  track_code: string
  contact_info: string
  researcher_names: []
  categories: []
  downloads: []
  date_published_available: string
  stage: string
  references: string
  technological_validation: string
  t2: string
  business: any

  constructor(data: any) {
    this.id = data.id ?? ''
    this.name = data.name ?? ''
    this.title_link = data.title_link ?? ''
    this.abstract = data.abstract ?? ''
    this.background = data.background ?? ''
    this.overview = data.overview ?? ''
    this.tech_description = data.tech_description ?? ''
    this.advantages = data.advantages ?? ''
    this.applications = data.applications ?? ''
    this.publications = data.publications ?? ''
    this.ip_status = data.ip_status ?? ''
    this.track_code = data.track_code ?? ''
    this.contact_info = data.contact_info ?? ''
    this.researcher_names = data.researcher_names ?? []
    this.categories = data.categories ?? []
    this.downloads = data.downloads ?? []
    this.date_published_available = data.date_published_available ?? ''
    this.stage = data.stage ?? ''
    this.references = data.references ?? ''
    this.technological_validation = data.technological_validation ?? ''
    this.t2 = data.t2 ?? ''
    this.business = data.business ?? {}
  }
}

export class EntityDocumentConcept {
  score: number
  aliases: string[]
  name: string
  id: string
  qid: string
  last_modified: string

  constructor(data: any) {
    this.score = data.score
    this.aliases = data.aliases
    this.name = data.name
    this.id = data.id
    this.qid = data.qid
    this.last_modified = data.last_modified
  }
}

export class DirectGraphBusinessEntity extends DirectGraphQueryEntity {
  company: string
  award_link: string

  entity_document?: EntityDocument
  entity_document_concept?: EntityDocumentConcept

  constructor(data: any) {
    super(data)
    this.company = data.company
    this.award_link = data.award_link
    this.unique_id = data.unique_id
    this.entity_document = data.entity_document
      ? new EntityDocument(data.entity_document)
      : undefined
    this.entity_document_concept = data.entity_document_concept
      ? new EntityDocumentConcept(data.entity_document_concept)
      : undefined
  }

  getDetailUrl(goBackToDiscoveryRequestUniqueId: string | undefined = undefined): string {
    return `/businesses/detail?externalId=${this.unique_id}${
      goBackToDiscoveryRequestUniqueId
        ? `&fromDiscoveryRequestUniqueId=${goBackToDiscoveryRequestUniqueId}`
        : ''
    }`
  }
}
interface IConceptDocSource {
  base_url: string
  business_id: string
  content: string
  domain: string
  extracted_at: string
  id: string
  is_home_page: boolean
  name: string
  url: string
}
interface IConceptDoc {
  id: string
  name: string
  source_id: string
  source_type: string
  text: string
  text_hash: string
}
interface IConcept {
  aliases: string[]
  id: string
  last_modified: string
  name: string
  qid: string
  score: number
  weight: number
}
export interface IV2Concept {
  __best_document: {
    id: string
    name: string
    source: {id: string; name: string}
    source_id: string
    text: string
    text_hash: string
  }
  id: string
}

export interface IConcepts {
  concept: IConcept
  document: IConceptDoc
  document_source: IConceptDocSource
  hits: number
  score: number
}

export class MBusinessEntity {
  company: string
  award_link: string
  unique_id: string
  entity?: MBusinessEntityDetail
  connections?: IConcepts[]

  constructor(data: any) {
    this.company = data.entity.name
    this.award_link = data.entity.website
    this.unique_id = data.entity.id
    this.entity = data.entity ? new MBusinessEntityDetail(data.entity) : undefined
    this.connections = data.connections
  }

  getDetailUrl(goBackToDiscoveryRequestUniqueId: string | undefined = undefined): string {
    return `/businesses/detail?externalId=${this.unique_id}${
      goBackToDiscoveryRequestUniqueId
        ? `&fromDiscoveryRequestUniqueId=${goBackToDiscoveryRequestUniqueId}`
        : ''
    }`
  }
}

export class MBusinessEntityV2 {
  company: string
  award_link: string
  unique_id: string
  entity?: MBusinessEntityDetail
  related_concepts?: IV2Concept[] | undefined

  constructor(data: any) {
    this.company = data.name
    this.award_link = data.website
    this.unique_id = data.id
    this.entity = data ? new MBusinessEntityDetail(data) : undefined
    this.related_concepts = data.related_concepts
  }

  getDetailUrl(goBackToDiscoveryRequestUniqueId: string | undefined = undefined): string {
    return `/businesses/detail?externalId=${this.unique_id}${
      goBackToDiscoveryRequestUniqueId
        ? `&fromDiscoveryRequestUniqueId=${goBackToDiscoveryRequestUniqueId}`
        : ''
    }`
  }
}

export class MAwardEntityV2 {
  entity?: MAwardEntityDetail
  related_concepts?: IV2Concept[] | undefined

  constructor(data: any) {
    this.entity = data ? new MAwardEntityDetail(data) : undefined
    this.related_concepts = data.related_concepts
  }

  getDetailUrl(goBackToDiscoveryRequestUniqueId: string | undefined = undefined): string {
    return `/businesses/detail?externalId=${this.entity?.business_id}${
      goBackToDiscoveryRequestUniqueId
        ? `&fromDiscoveryRequestUniqueId=${goBackToDiscoveryRequestUniqueId}`
        : ''
    }`
  }
}

export class MAwardEntity {
  entity?: MAwardEntityDetail
  connections?: IConcepts[]

  constructor(data: any) {
    this.entity = data.entity ? new MAwardEntityDetail(data.entity) : undefined
    this.connections = data.connections
  }

  getDetailUrl(goBackToDiscoveryRequestUniqueId: string | undefined = undefined): string {
    return `/businesses/detail?externalId=${this.entity?.business_id}${
      goBackToDiscoveryRequestUniqueId
        ? `&fromDiscoveryRequestUniqueId=${goBackToDiscoveryRequestUniqueId}`
        : ''
    }`
  }
}

export class MInnovationEntity {
  entity?: MInnovationEntityDetail
  connections?: IConcepts[]

  constructor(data: any) {
    this.entity = data.entity ? new MInnovationEntityDetail(data.entity) : undefined
    this.connections = data.connections
  }

  getDetailUrl(goBackToDiscoveryRequestUniqueId: string | undefined = undefined): string {
    return `/innovations/detail?innovationId=${this.entity?.id}${
      goBackToDiscoveryRequestUniqueId
        ? `&fromDiscoveryRequestUniqueId=${goBackToDiscoveryRequestUniqueId}`
        : ''
    }`
  }
}

export class MOpportunityEntity {
  entity?: MOpportunityEntityDetail
  connections?: IConcepts[]

  constructor(data: any) {
    this.entity = data.entity ? new MOpportunityEntityDetail(data.entity) : undefined
    this.connections = data.connections
  }

  getDetailUrl(goBackToDiscoveryRequestUniqueId: string | undefined = undefined): string {
    return `/opportunities/detail?innovationId=${this.entity?.id}${
      goBackToDiscoveryRequestUniqueId
        ? `&fromDiscoveryRequestUniqueId=${goBackToDiscoveryRequestUniqueId}`
        : ''
    }`
  }
}

export class MOpportunityEntityV2 {
  entity?: MOpportunityEntityDetail
  related_concepts?: IV2Concept[] | undefined

  constructor(data: any) {
    this.entity = data ? new MOpportunityEntityDetail(data) : undefined
    this.related_concepts = data.related_concepts
  }

  getDetailUrl(goBackToDiscoveryRequestUniqueId: string | undefined = undefined): string {
    return `/opportunities/detail?opportunityId=${this.entity?.id}${
      goBackToDiscoveryRequestUniqueId
        ? `&fromDiscoveryRequestUniqueId=${goBackToDiscoveryRequestUniqueId}`
        : ''
    }`
  }
}

export class MInnovationEntityV2 {
  entity?: MInnovationEntityDetail
  related_concepts?: IV2Concept[] | undefined

  constructor(data: any) {
    this.entity = data ? new MInnovationEntityDetail(data) : undefined
    this.related_concepts = data.related_concepts
  }

  getDetailUrl(goBackToDiscoveryRequestUniqueId: string | undefined = undefined): string {
    return `/innovations/detail?innovationId=${this.entity?.id}${
      goBackToDiscoveryRequestUniqueId
        ? `&fromDiscoveryRequestUniqueId=${goBackToDiscoveryRequestUniqueId}`
        : ''
    }`
  }
}

export class DirectGraphAwardEntity extends DirectGraphQueryEntity {
  agency: string
  award_link: string
  company: string
  year: number

  constructor(data: any) {
    super(data)
    this.agency = data.agency
    this.award_link = data.award_link
    this.company = data.company
    this.year = data.year
  }

  localizedSubtitle(): string {
    // TODO: Extend backend query to return dollar amount as float
    try {
      // try to take $224481.0 into $224,481
      const amountWithoutDollarSign = this.subtitle.replace('$', '')
      // parse float
      const amountAsFloat = parseFloat(amountWithoutDollarSign)
      // format as currency
      const amountAsCurrency = amountAsFloat.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
      return amountAsCurrency
    } catch (e) {
      return this.subtitle
    }
  }

  getDetailUrl(goBackToDiscoveryRequestUniqueId: string | undefined = undefined): string {
    // Until we have the company_id on an award entity item we return #
    return `/businesses/detail?externalId=${this.company}${
      goBackToDiscoveryRequestUniqueId
        ? `&fromDiscoveryRequestUniqueId=${goBackToDiscoveryRequestUniqueId}&tab=awards`
        : ''
    }`
  }
}

export class BusinessUpload {
  created_at: string
  updated_at: string
  unique_id: string
  file_upload: FileUpload

  constructor(data: any) {
    this.created_at = data.created_at
    this.updated_at = data.updated_at
    this.unique_id = data.unique_id
    this.file_upload = new FileUpload(data.file_upload)
  }
}
